import { VOICE_FALLBACK_LANGUAGES } from '@ast-data/shared';
import { authFetch } from '../../utils/authFetch';
import {
    TranslationRequestError,
    type TranslationFailure
} from './translationApi';

/** A spoken language as DeepL Voice names it: 'de', 'pl', 'pt' … */
export type VoiceLanguage = string;

let languagesRequest: Promise<VoiceLanguage[]> | null = null;

/**
 * The languages DeepL Voice can listen to, as the server last heard them from
 * DeepL. Asked once per page load; a failed request is asked again next time.
 */
export function fetchVoiceLanguages(): Promise<VoiceLanguage[]> {
    if (!languagesRequest) {
        languagesRequest = authFetch('/api/translate/voice/languages')
            .then((response) => (response.ok ? response.json() : null))
            .then((body: { languages?: string[] } | null) => {
                if (!body?.languages?.length) {
                    throw new Error('no languages');
                }
                return body.languages;
            })
            .catch(() => {
                languagesRequest = null;
                return [...VOICE_FALLBACK_LANGUAGES];
            });
    }
    return languagesRequest;
}

export interface VoiceSession {
    streamingUrl: string;
    token: string;
    /** The usage log's row, to report the recording's length to. */
    usageId: string;
}

const KNOWN_FAILURES: readonly TranslationFailure[] = [
    'notConfigured',
    'quotaExceeded',
    'rateLimited',
    'unavailable'
];

/** Ask the server to open a DeepL Voice session for one recording. */
export async function requestVoiceSession(body: {
    /** Left out, DeepL detects the language. */
    sourceLang?: VoiceLanguage;
    translate: boolean;
    projectId?: string;
}): Promise<VoiceSession> {
    let response: Response;
    try {
        response = await authFetch('/api/translate/voice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
    } catch {
        throw new TranslationRequestError('offline');
    }
    if (!response.ok) {
        const error = (await response.json().catch(() => ({}))) as {
            error?: string;
        };
        const reason = KNOWN_FAILURES.find(
            (failure) => failure === error.error
        );
        throw new TranslationRequestError(reason ?? 'unavailable');
    }
    return (await response.json()) as VoiceSession;
}

/** Book the recording's length. Best effort: a lost report costs nothing. */
export function reportVoiceSeconds(usageId: string, seconds: number): void {
    void authFetch(
        `/api/translate/voice/${encodeURIComponent(usageId)}/seconds`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ seconds: Math.round(seconds) })
        }
    ).catch(() => {});
}
